import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Bell, Send, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { getReminderSettings, updateReminderSettings, triggerDailyReminders } from '../../services/taskApi';
import { toast } from 'sonner';

const TaskSettings = () => {
  const { isDark } = useOutletContext();
  const [settings, setSettings] = useState({ enabled: false, time: '10:00', email_enabled: true, whatsapp_enabled: true });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getReminderSettings();
        setSettings(res.data || { enabled: false, time: '10:00', email_enabled: true, whatsapp_enabled: true });
      } catch (err) { console.error('TaskSettings: load reminder settings failed', err); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleSave = async () => {
    try {
      await updateReminderSettings(settings);
      toast.success('Reminder settings saved');
    } catch { toast.error('Failed'); }
  };

  const handleTestReminders = async () => {
    setSending(true);
    try {
      await triggerDailyReminders();
      toast.success('Daily reminders triggered for all users');
    } catch { toast.error('Failed - check SMTP/Twilio settings in CMS Admin'); }
    finally { setSending(false); }
  };

  const cardCls = isDark ? 'border-0 bg-white/5' : 'border border-gray-200 bg-white shadow-sm';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900';
  const labelCls = isDark ? 'text-white/70' : 'text-gray-600';

  if (loading) return <div className="flex items-center justify-center h-32"><div className="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-lg" data-testid="task-settings">
      <div>
        <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Notification Settings</h1>
        <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Configure daily task reminders</p>
      </div>

      <Card className={cardCls}>
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center"><Bell size={20} className="text-blue-400" /></div>
              <div><p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Daily Pending Task Reminder</p><p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Send summary to all users</p></div>
            </div>
            <Switch checked={settings.enabled} onCheckedChange={v => setSettings(s => ({ ...s, enabled: v }))} />
          </div>

          <div>
            <Label className={`${labelCls} flex items-center gap-1.5`}><Clock size={14} /> Reminder Time</Label>
            <Input type="time" value={settings.time} onChange={e => setSettings(s => ({ ...s, time: e.target.value }))} className={`${inputCls} mt-1 w-40`} data-testid="reminder-time" />
            <p className={`text-[10px] mt-1 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>Daily at this time (requires SMTP/Twilio configured in CMS Settings)</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Switch checked={settings.email_enabled} onCheckedChange={v => setSettings(s => ({ ...s, email_enabled: v }))} />
              <Label className={isDark ? 'text-white/60' : 'text-gray-600'}>Send via Email</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={settings.whatsapp_enabled} onCheckedChange={v => setSettings(s => ({ ...s, whatsapp_enabled: v }))} />
              <Label className={isDark ? 'text-white/60' : 'text-gray-600'}>Send via WhatsApp</Label>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-reminder-settings">Save Settings</Button>
            <Button onClick={handleTestReminders} variant="outline" className={isDark ? 'border-white/10 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} disabled={sending}>
              <Send size={14} className="mr-2" /> {sending ? 'Sending...' : 'Test Send Now'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className={cardCls}>
        <CardContent className="p-4">
          <p className={`text-xs leading-relaxed ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
            <b className={isDark ? 'text-white/60' : 'text-gray-700'}>Important:</b> Email and WhatsApp notifications require SMTP and Twilio credentials to be configured in the main CMS Admin panel (Settings &gt; Email &amp; WhatsApp). Without these credentials, notifications will be skipped silently.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaskSettings;
